import { type ReactNode, useEffect } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import { LoomLogo } from '@atlaskit/logo';
import { token } from '@atlaskit/tokens';
import { JiraBackground } from '@atlassian/product-wireframes';

import { useAnalyticsClient } from '@/analytics/wac-analytics/analytics-client';
import { Modal } from '@/components/signup/layouts/modal';
import { Image } from '@/libraries/design/components/image/src/image';
import { wacToken } from '@/libraries/design/foundations';

import loomCollaborationPanel from './assets/loom-collaboration-panel.png';
import twcOthersLogos from './assets/twc-others.svg';
import { useChoiceRedirect } from './choice-urls';
import {
	type ChoiceValue,
	buildTwcFeatures,
	CancelAnytimeLink,
	CustomerAgreementLink,
	FeatureList,
	PrivacyPolicyLink,
} from './loom-choice-variant-a';
import { DEFAULT_TWC_PRICE, messages } from './messages';
import { type LoomEdition, type TwcPriceMode } from './use-twc-price';

/**
 * TWCG-2108 Loom Land opt-in — "Variant B" design.
 *
 * A single-option variant (no radio choice): it presents only the Teamwork
 * Collection pitch with one primary CTA, on the same 50/50 modal (content left,
 * marketing graphic right) as Variant A. Copy, feature icons, legal links,
 * typography and the right-side graphic are all shared with Variant A so the two
 * variants stay visually consistent.
 */

// Re-export Variant A's ChoiceValue so both variants share one source of truth
// and can't drift if a third option is ever added.
export type LoomChoiceVariantBValue = ChoiceValue;

const CONTENT_TEST_ID = 'loom-land-opt-in-variant-b';

// Figma content height (px); both columns share it so the 50/50 split reads as a
// full-height panel (matches Variant A).
const MODAL_HEIGHT = 600;

const layoutCss = css({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'stretch',
	minHeight: `${MODAL_HEIGHT}px`,
	width: '100%',
});

const contentCss = css({
	display: 'flex',
	flexDirection: 'column',
	// Content is top-aligned; the footer (legal + buttons) is pushed to the
	// bottom via marginBlockStart:auto so it pins to the bottom of the column.
	// A generous gap gives the sections even breathing room.
	gap: token('space.300'),
	// Matches Figma "Content" frame padding: 48px all sides.
	padding: token('space.600'),
	flex: '1 1 50%',
	width: '50%',
	minWidth: 0,
	boxSizing: 'border-box',
});

// Action buttons aligned to the bottom-right and pinned to the bottom of the
// content column (matches Figma Variant B). The legal disclaimer sits directly
// above, just under the feature list, rather than at the very bottom.
const actionsCss = css({
	display: 'flex',
	justifyContent: 'flex-end',
	alignItems: 'center',
	gap: token('space.100'),
	marginBlockStart: 'auto',
});

// TODO(TWCG-2108): Post-experiment polish — decompose the baked full-panel PNG.
// The background (Blue200 field + swirl curve + square cross-hatch grid) is baked
// into the panel PNG and duplicated across variants. The scalable approach is to
// layer the background as reusable SVGs and overlay only the lightweight
// illustration per variant on top (see the matching note in Variant A).
//
// Right-hand graphic panel — the other half of the even split (same asset as
// Variant A). Hidden on narrow viewports so the content stays usable.
const graphicPanelCss = css({
	display: 'none',
	flex: '1 1 50%',
	width: '50%',
	minWidth: 0,
	alignSelf: 'stretch',
	overflow: 'hidden',
	backgroundColor: wacToken('color.extended.blue200'),
	backgroundImage: `url(${loomCollaborationPanel})`,
	backgroundSize: '100% auto',
	backgroundPosition: 'top center',
	backgroundRepeat: 'no-repeat',
	'@media (min-width: 768px)': {
		display: 'block',
	},
});

const headingCss = css({
	textWrap: 'balance',
});

const logosCss = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	font: token('font.body'),
	fontWeight: token('font.weight.regular'),
});

const plusCss = css({
	color: token('color.text.subtle'),
	fontWeight: token('font.weight.regular'),
});

// Combined product-logos graphic (Jira, Confluence, Trello, Rovo), sized to
// match the Loom wordmark height beside it.
const appTilesCss = css({
	display: 'block',
	height: '30px',
	width: 'auto',
});

const subtitleCss = css({
	margin: 0,
	font: token('font.body'),
	fontWeight: token('font.weight.medium'),
	color: token('color.text'),
});

const legalCss = css({
	margin: 0,
	font: token('font.body.small'),
	color: token('color.text.subtlest'),
});

// Legal disclaimer with inline links. Reuses the shared, module-level link
// renderers from Variant A so copy, destinations, and a11y behaviour stay in
// sync across both variants.
const LegalText = () => (
	<p css={legalCss}>
		<FormattedMessage
			{...messages.legalTwc}
			values={{
				customerAgreement: CustomerAgreementLink,
				privacyPolicy: PrivacyPolicyLink,
				cancelAnytime: CancelAnytimeLink,
			}}
		/>
	</p>
);

export type LoomChoiceVariantBProps = {
	/**
	 * Optional callback invoked when the user confirms via a CTA, before the
	 * redirect to signup/provisioning (e.g. for analytics). The CTA itself always
	 * navigates to the choice's continue URL — pass a custom {@link onSelect} only
	 * to hook additional behaviour, not to override routing.
	 */
	onSelect?: (choice: LoomChoiceVariantBValue) => void;
	/**
	 * Monthly per-user upsell price shown in the pricing feature line. Passed as
	 * an ICU argument so the amount (or currency) can change without editing the
	 * translated copy. Defaults to {@link DEFAULT_TWC_PRICE}.
	 */
	price?: ReactNode;
	/** Price message to render for {@link price}; defaults to "more". */
	priceMode?: TwcPriceMode;
	/** Loom edition, used to pick the provisioned TWC edition; defaults to "premium". */
	edition?: LoomEdition;
	/** Original page query string, passed through to the Loom-only signup redirect. */
	search?: string;
};

export const LoomChoiceVariantB = ({
	onSelect,
	price = DEFAULT_TWC_PRICE,
	priceMode = 'more',
	edition = 'premium',
	search = '',
}: LoomChoiceVariantBProps) => {
	const twcFeatures = buildTwcFeatures(price, priceMode);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsClient = useAnalyticsClient();
	const redirectToChoice = useChoiceRedirect(edition, search);

	// Confirming a choice fires the optional callback (analytics/side effects)
	// then navigates to that choice's signup/provisioning flow:
	//  - teamwork-collection → ?bundle=teamwork-collection&edition=premium
	//  - loom-only           → today's Loom-only flow (same as control redirect)
	const handleSelect = (selected: LoomChoiceVariantBValue) => {
		onSelect?.(selected);
		redirectToChoice(selected);
	};

	// Fire the modal "viewed" screen event once shown (matches Variant A). The
	// `variant` attribute differentiates the arm, so a single screen event covers
	// both variants — no separate component-viewed event needed. Guarded on
	// analyticsClient so it safely no-ops outside the live signup flow (Storybook).
	useEffect(() => {
		if (!analyticsClient) {
			return;
		}
		createAnalyticsEvent({
			action: 'viewed',
			actionSubject: 'modal',
			name: 'loomLandOptInModal',
			eventType: 'screen',
			attributes: { variant: 'b' },
		}).fire();
	}, [analyticsClient, createAnalyticsEvent]);

	return (
		<JiraBackground>
			<Modal
				size="md"
				productLogo={<LoomLogo appearance="brand" size="medium" shouldUseNewLogoDesign />}
			>
				<div css={layoutCss}>
					<div css={contentCss} data-testid={CONTENT_TEST_ID}>
						<span css={logosCss}>
							<LoomLogo appearance="brand" size="small" shouldUseNewLogoDesign />
							<span css={plusCss} aria-hidden="true">
								+
							</span>
							<Image css={appTilesCss} src={twcOthersLogos} alt="Jira, Confluence, Trello, Rovo" />
						</span>

						<div css={headingCss}>
							<Heading size="large" as="h1">
								<FormattedMessage {...messages.heading} />
							</Heading>
						</div>

						<p css={subtitleCss}>
							<FormattedMessage {...messages.twcSubtitle} />
						</p>

						<FeatureList features={twcFeatures} />

						<LegalText />

						<div css={actionsCss}>
							<Button appearance="subtle" onClick={() => handleSelect('loom-only')}>
								<FormattedMessage {...messages.variantBLoomOnlyCta} />
							</Button>
							<Button appearance="primary" onClick={() => handleSelect('teamwork-collection')}>
								<FormattedMessage {...messages.twcCta} />
							</Button>
						</div>
					</div>

					<div css={graphicPanelCss} role="presentation" />
				</div>
			</Modal>
		</JiraBackground>
	);
};
